const INVENTORY_REASON = "inventory_adjustment";
const SALE_REASONS = new Set(["sale", "sale_adjustment", "cancel"]);

const getMovementTime = (movement) => Number(movement?.createdAt) || 0;
const getMovementDelta = (movement) => Number(movement?.delta) || 0;

const calculateStockLossStats = (movements = []) => {
  const sortedMovements = [...movements].sort((a, b) => getMovementTime(a) - getMovementTime(b));
  const inventoryMovements = sortedMovements.filter(
    (movement) => movement?.reason === INVENTORY_REASON,
  );
  const latestInventoryMovement = inventoryMovements[inventoryMovements.length - 1] || null;

  if (!latestInventoryMovement) {
    return {
      hasInventoryCount: false,
      soldQuantity: 0,
      inventoryDelta: 0,
      lossQuantity: 0,
      surplusQuantity: 0,
      lossRate: null,
    };
  }

  const previousInventoryMovement = inventoryMovements[inventoryMovements.length - 2] || null;
  const periodStart = previousInventoryMovement ? getMovementTime(previousInventoryMovement) : 0;
  const periodEnd = getMovementTime(latestInventoryMovement);

  const netSalesDelta = sortedMovements.reduce((total, movement) => {
    const movementTime = getMovementTime(movement);
    if (
      !SALE_REASONS.has(movement?.reason) ||
      movementTime <= periodStart ||
      movementTime > periodEnd
    ) {
      return total;
    }

    return total + getMovementDelta(movement);
  }, 0);

  const soldQuantity = Math.max(-netSalesDelta, 0);
  const inventoryDelta = getMovementDelta(latestInventoryMovement);
  const lossQuantity = Math.max(-inventoryDelta, 0);
  const surplusQuantity = Math.max(inventoryDelta, 0);

  return {
    hasInventoryCount: true,
    lastInventoryAt: getMovementTime(latestInventoryMovement),
    soldQuantity,
    inventoryDelta,
    lossQuantity,
    surplusQuantity,
    lossRate: soldQuantity > 0 ? (lossQuantity / soldQuantity) * 100 : null,
  };
};

const calculateStockLossStatsByItem = (movements = []) => {
  const movementsByStockItemId = movements.reduce((acc, movement) => {
    if (!movement?.stockItemId) return acc;
    if (!acc[movement.stockItemId]) acc[movement.stockItemId] = [];
    acc[movement.stockItemId].push(movement);
    return acc;
  }, {});

  return Object.fromEntries(
    Object.entries(movementsByStockItemId).map(([stockItemId, stockItemMovements]) => [
      stockItemId,
      calculateStockLossStats(stockItemMovements),
    ]),
  );
};

const calculateStockLossSummary = (lossStatsByItem = {}) => {
  const summary = Object.values(lossStatsByItem).reduce(
    (acc, stats) => {
      if (!stats?.hasInventoryCount) return acc;
      acc.soldQuantity += stats.soldQuantity;
      acc.lossQuantity += stats.lossQuantity;
      acc.surplusQuantity += stats.surplusQuantity;
      acc.itemsWithInventoryCount += 1;
      if (stats.lossQuantity > 0) acc.itemsWithLoss += 1;
      return acc;
    },
    {
      soldQuantity: 0,
      lossQuantity: 0,
      surplusQuantity: 0,
      itemsWithInventoryCount: 0,
      itemsWithLoss: 0,
    },
  );

  return {
    ...summary,
    lossRate:
      summary.itemsWithInventoryCount > 0 && summary.soldQuantity > 0
        ? (summary.lossQuantity / summary.soldQuantity) * 100
        : null,
  };
};

const getStockLossTone = (lossRate) => {
  if (!Number.isFinite(lossRate) || lossRate <= 2) return "success";
  if (lossRate <= 5) return "warning";
  return "danger";
};

export {
  calculateStockLossStats,
  calculateStockLossStatsByItem,
  calculateStockLossSummary,
  getStockLossTone,
};
